// src/features/team/logic.ts
// Framework-agnostic business logic for Teams.
// Zero React imports — pure TypeScript functions and data maps.

import type { Team } from '@core/types/team';
import type { Entity } from '@core/types/entity';

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

export function formatDate(dateStr: string | undefined | null, locale = 'en'): string {
  if (!dateStr) return '--';
  // Go zero time ('0001-01-01…') means the field was never set.
  if (new Date(dateStr).getFullYear() < 1971) return '--';
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(dateStr));
  } catch {
    return String(dateStr);
  }
}

export function formatDateTime(dateStr: string | undefined | null, locale = 'en'): string {
  if (!dateStr) return '--';
  // Go zero time ('0001-01-01…') means the field was never set.
  if (new Date(dateStr).getFullYear() < 1971) return '--';
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateStr));
  } catch {
    return String(dateStr);
  }
}

export function formatRelativeTime(dateStr: string | undefined | null, locale = 'en'): string {
  if (!dateStr) return '--';
  // Go zero time ('0001-01-01…') means the field was never set.
  if (new Date(dateStr).getFullYear() < 1971) return '--';
  try {
    const diff = Date.now() - new Date(dateStr).getTime();
    const sec = Math.floor(diff / 1000);
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'long' });
    if (sec < 60) return rtf.format(-sec, 'second');
    const min = Math.floor(sec / 60);
    if (min < 60) return rtf.format(-min, 'minute');
    const hr = Math.floor(min / 60);
    if (hr < 24) return rtf.format(-hr, 'hour');
    const day = Math.floor(hr / 24);
    if (day < 7) return rtf.format(-day, 'day');
    if (day < 30) return rtf.format(-Math.floor(day / 7), 'week');
    return formatDate(dateStr, locale);
  } catch {
    return String(dateStr);
  }
}

// ---------------------------------------------------------------------------
// Avatar helpers
// ---------------------------------------------------------------------------

const AVATAR_COLORS = [
  'hsl(239 84% 67%)',   // indigo
  'hsl(330 81% 60%)',   // pink
  'hsl(38 92% 50%)',    // amber
  'hsl(160 84% 39%)',   // emerald
  'hsl(217 91% 60%)',   // blue
  'hsl(258 90% 66%)',   // violet
  'hsl(0 84% 60%)',     // red
  'hsl(174 84% 40%)',   // teal
];

export function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function getInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// ---------------------------------------------------------------------------
// Status → Badge variant maps (return i18n keys for labels)
// ---------------------------------------------------------------------------

export type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'primary' | 'secondary';

interface StatusMapping {
  variant: BadgeVariant;
  labelKey: string;
}

export const teamStatusMap: Record<string, StatusMapping> = {
  TEAM_STATUS_ACTIVE: { variant: 'success', labelKey: 'team.status.active' },
  TEAM_STATUS_INACTIVE: { variant: 'warning', labelKey: 'team.status.inactive' },
  TEAM_STATUS_ARCHIVED: { variant: 'secondary', labelKey: 'team.status.archived' },
};

export const membershipStatusMap: Record<string, StatusMapping> = {
  MEMBERSHIP_STATUS_PENDING: { variant: 'warning', labelKey: 'team.memberStatus.pending' },
  MEMBERSHIP_STATUS_ACTIVE: { variant: 'success', labelKey: 'team.memberStatus.active' },
  MEMBERSHIP_STATUS_SUSPENDED: { variant: 'error', labelKey: 'team.memberStatus.suspended' },
  MEMBERSHIP_STATUS_LEFT: { variant: 'secondary', labelKey: 'team.memberStatus.left' },
  MEMBERSHIP_STATUS_REMOVED: { variant: 'secondary', labelKey: 'team.memberStatus.removed' },
};

export const invitationStatusMap: Record<string, StatusMapping> = {
  INVITATION_STATUS_PENDING: { variant: 'warning', labelKey: 'team.invitationStatus.pending' },
  INVITATION_STATUS_ACCEPTED: { variant: 'success', labelKey: 'team.invitationStatus.accepted' },
  INVITATION_STATUS_DECLINED: { variant: 'error', labelKey: 'team.invitationStatus.declined' },
  INVITATION_STATUS_EXPIRED: { variant: 'secondary', labelKey: 'team.invitationStatus.expired' },
  INVITATION_STATUS_CANCELLED: { variant: 'secondary', labelKey: 'team.invitationStatus.cancelled' },
};

export const applicationStatusMap: Record<string, StatusMapping> = {
  APPLICATION_STATUS_PENDING: { variant: 'warning', labelKey: 'team.appStatus.pending' },
  APPLICATION_STATUS_APPROVED: { variant: 'success', labelKey: 'team.appStatus.approved' },
  APPLICATION_STATUS_REJECTED: { variant: 'error', labelKey: 'team.appStatus.rejected' },
  APPLICATION_STATUS_CANCELLED: { variant: 'secondary', labelKey: 'team.appStatus.cancelled' },
};

export const delegationStatusMap: Record<string, StatusMapping> = {
  DELEGATION_STATUS_PENDING: { variant: 'warning', labelKey: 'team.delegationStatus.pending' },
  DELEGATION_STATUS_APPROVED: { variant: 'primary', labelKey: 'team.delegationStatus.approved' },
  DELEGATION_STATUS_ACTIVE: { variant: 'success', labelKey: 'team.delegationStatus.active' },
  DELEGATION_STATUS_COMPLETED: { variant: 'secondary', labelKey: 'team.delegationStatus.completed' },
  DELEGATION_STATUS_CANCELLED: { variant: 'secondary', labelKey: 'team.delegationStatus.cancelled' },
  DELEGATION_STATUS_REJECTED: { variant: 'error', labelKey: 'team.delegationStatus.rejected' },
};

export const scheduleStatusMap: Record<string, StatusMapping> = {
  ROLE_SCHEDULE_STATUS_ACTIVE: { variant: 'success', labelKey: 'team.scheduleStatus.active' },
  ROLE_SCHEDULE_STATUS_PAUSED: { variant: 'warning', labelKey: 'team.scheduleStatus.paused' },
  ROLE_SCHEDULE_STATUS_COMPLETED: { variant: 'secondary', labelKey: 'team.scheduleStatus.completed' },
};

export const temporaryRoleStatusMap: Record<string, StatusMapping> = {
  TEMPORARY_ASSIGNMENT_STATUS_ACTIVE: { variant: 'success', labelKey: 'team.tempRoleStatus.active' },
  TEMPORARY_ASSIGNMENT_STATUS_EXPIRED: { variant: 'secondary', labelKey: 'team.tempRoleStatus.expired' },
  TEMPORARY_ASSIGNMENT_STATUS_CANCELLED: { variant: 'secondary', labelKey: 'team.tempRoleStatus.cancelled' },
};

export const transferStatusMap: Record<string, StatusMapping> = {
  APPLICATION_STATUS_PENDING: { variant: 'warning', labelKey: 'team.appStatus.pending' },
  APPLICATION_STATUS_APPROVED: { variant: 'success', labelKey: 'team.appStatus.approved' },
  APPLICATION_STATUS_REJECTED: { variant: 'error', labelKey: 'team.appStatus.rejected' },
  APPLICATION_STATUS_CANCELLED: { variant: 'secondary', labelKey: 'team.appStatus.cancelled' },
  TRANSFER_STATUS_PENDING: { variant: 'warning', labelKey: 'team.appStatus.pending' },
  TRANSFER_STATUS_COMPLETED: { variant: 'success', labelKey: 'team.appStatus.approved' },
  TRANSFER_STATUS_CANCELLED: { variant: 'secondary', labelKey: 'team.appStatus.cancelled' },
};

export function getStatusMapping(
  map: Record<string, StatusMapping>,
  status: string,
): StatusMapping {
  return map[status] ?? { variant: 'secondary', labelKey: 'common.unknown' };
}

// ---------------------------------------------------------------------------
// Enum label maps (return i18n keys)
// ---------------------------------------------------------------------------

export const teamTypeMap: Record<string, { labelKey: string; iconKey: string; descriptionKey: string; iconPath: string }> = {
  TEAM_TYPE_COMPANY: {
    labelKey: 'team.type.company',
    iconKey: 'building',
    descriptionKey: 'team.type.companyDesc',
    iconPath: 'M3 21h18M5 21V7l7-4 7 4v14M9 10h.01M15 10h.01M9 14h.01M15 14h.01M10 21v-4h4v4',
  },
  TEAM_TYPE_PROJECT: {
    labelKey: 'team.type.project',
    iconKey: 'folder',
    descriptionKey: 'team.type.projectDesc',
    iconPath: 'M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z',
  },
  TEAM_TYPE_DEPARTMENT: {
    labelKey: 'team.type.department',
    iconKey: 'building',
    descriptionKey: 'team.type.departmentDesc',
    iconPath: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  },
  TEAM_TYPE_WORKGROUP: {
    labelKey: 'team.type.workgroup',
    iconKey: 'users',
    descriptionKey: 'team.type.workgroupDesc',
    iconPath: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
  },
  TEAM_TYPE_COMMUNITY: {
    labelKey: 'team.type.community',
    iconKey: 'globe',
    descriptionKey: 'team.type.communityDesc',
    iconPath: 'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9',
  },
};

export const teamVisibilityMap: Record<string, { labelKey: string; descriptionKey: string; iconPath: string }> = {
  TEAM_VISIBILITY_PUBLIC: {
    labelKey: 'team.visibility.public',
    descriptionKey: 'team.visibility.publicDesc',
    iconPath: 'M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  },
  TEAM_VISIBILITY_PRIVATE: {
    labelKey: 'team.visibility.private',
    descriptionKey: 'team.visibility.privateDesc',
    iconPath: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
  },
  TEAM_VISIBILITY_SECRET: {
    labelKey: 'team.visibility.secret',
    descriptionKey: 'team.visibility.secretDesc',
    iconPath: 'M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21',
  },
};

export const delegationReasonMap: Record<string, string> = {
  DELEGATION_REASON_VACATION: 'team.delegationReason.vacation',
  DELEGATION_REASON_SICK_LEAVE: 'team.delegationReason.sickLeave',
  DELEGATION_REASON_BUSINESS_TRIP: 'team.delegationReason.businessTrip',
  DELEGATION_REASON_PARENTAL_LEAVE: 'team.delegationReason.parentalLeave',
  DELEGATION_REASON_TRAINING: 'team.delegationReason.training',
  DELEGATION_REASON_TEMPORARY: 'team.delegationReason.temporary',
  DELEGATION_REASON_OTHER: 'team.delegationReason.other',
};

export const scheduleTypeMap: Record<string, string> = {
  SCHEDULE_TYPE_ROTATION: 'team.scheduleType.rotation',
  SCHEDULE_TYPE_CALENDAR: 'team.scheduleType.calendar',
  SCHEDULE_TYPE_RECURRING: 'team.scheduleType.recurring',
};

export const rotationPatternMap: Record<string, string> = {
  ROTATION_PATTERN_ROUND_ROBIN: 'team.rotationPattern.roundRobin',
  ROTATION_PATTERN_RANDOM: 'team.rotationPattern.random',
  ROTATION_PATTERN_WEIGHTED: 'team.rotationPattern.weighted',
  ROTATION_PATTERN_MANUAL: 'team.rotationPattern.manual',
};

export const automationEventTypeMap: Record<string, string> = {
  ROLE_AUTOMATION_EVENT_TYPE_SCHEDULE_CREATED: 'team.automationEventType.scheduleCreated',
  ROLE_AUTOMATION_EVENT_TYPE_SCHEDULE_STARTED: 'team.automationEventType.scheduleStarted',
  ROLE_AUTOMATION_EVENT_TYPE_SCHEDULE_PAUSED: 'team.automationEventType.schedulePaused',
  ROLE_AUTOMATION_EVENT_TYPE_SCHEDULE_RESUMED: 'team.automationEventType.scheduleResumed',
  ROLE_AUTOMATION_EVENT_TYPE_ROTATION_OCCURRED: 'team.automationEventType.rotationOccurred',
  ROLE_AUTOMATION_EVENT_TYPE_ROTATION_ACKNOWLEDGED: 'team.automationEventType.rotationAcknowledged',
  ROLE_AUTOMATION_EVENT_TYPE_ROTATION_SKIPPED: 'team.automationEventType.rotationSkipped',
  ROLE_AUTOMATION_EVENT_TYPE_DELEGATION_REQUESTED: 'team.automationEventType.delegationRequested',
  ROLE_AUTOMATION_EVENT_TYPE_DELEGATION_APPROVED: 'team.automationEventType.delegationApproved',
  ROLE_AUTOMATION_EVENT_TYPE_DELEGATION_REJECTED: 'team.automationEventType.delegationRejected',
  ROLE_AUTOMATION_EVENT_TYPE_DELEGATION_STARTED: 'team.automationEventType.delegationStarted',
  ROLE_AUTOMATION_EVENT_TYPE_DELEGATION_ENDED: 'team.automationEventType.delegationEnded',
  ROLE_AUTOMATION_EVENT_TYPE_DELEGATION_CANCELLED: 'team.automationEventType.delegationCancelled',
  ROLE_AUTOMATION_EVENT_TYPE_DELEGATION_EXTENDED: 'team.automationEventType.delegationExtended',
  ROLE_AUTOMATION_EVENT_TYPE_TEMP_ASSIGNMENT_CREATED: 'team.automationEventType.tempAssignmentCreated',
  ROLE_AUTOMATION_EVENT_TYPE_TEMP_ASSIGNMENT_STARTED: 'team.automationEventType.tempAssignmentStarted',
  ROLE_AUTOMATION_EVENT_TYPE_TEMP_ASSIGNMENT_ENDED: 'team.automationEventType.tempAssignmentEnded',
  ROLE_AUTOMATION_EVENT_TYPE_TEMP_ASSIGNMENT_CANCELLED: 'team.automationEventType.tempAssignmentCancelled',
};

export const automationEventCategoryMap: Record<string, BadgeVariant> = {
  schedule: 'primary',
  delegation: 'warning',
  temp: 'secondary',
};

export function getAutomationEventCategory(eventType: string): string {
  if (eventType.includes('SCHEDULE') || eventType.includes('ROTATION')) return 'schedule';
  if (eventType.includes('DELEGATION')) return 'delegation';
  if (eventType.includes('TEMP')) return 'temp';
  return 'secondary';
}

export const auditActionMap: Record<string, BadgeVariant> = {
  created: 'success',
  updated: 'primary',
  deleted: 'error',
  member_added: 'success',
  member_removed: 'error',
  role_assigned: 'primary',
  role_revoked: 'warning',
  invitation_sent: 'primary',
  settings_updated: 'primary',
  ownership_transferred: 'warning',
};

// ---------------------------------------------------------------------------
// Filter types
// ---------------------------------------------------------------------------

export type TeamFilter = 'all' | 'company' | 'project' | 'department' | 'workgroup' | 'community';

export const teamFilterToType: Record<TeamFilter, string | null> = {
  all: null,
  company: 'TEAM_TYPE_COMPANY',
  project: 'TEAM_TYPE_PROJECT',
  department: 'TEAM_TYPE_DEPARTMENT',
  workgroup: 'TEAM_TYPE_WORKGROUP',
  community: 'TEAM_TYPE_COMMUNITY',
};

export const teamFilters: { key: TeamFilter; labelKey: string }[] = [
  { key: 'all', labelKey: 'common.all' },
  { key: 'company', labelKey: 'team.type.company' },
  { key: 'project', labelKey: 'team.type.project' },
  { key: 'department', labelKey: 'team.type.department' },
  { key: 'workgroup', labelKey: 'team.type.workgroup' },
  { key: 'community', labelKey: 'team.type.community' },
];

export function filterTeams(
  teams: Team[],
  search: string,
  typeFilter: TeamFilter,
): Team[] {
  let filtered = teams;

  const mappedType = teamFilterToType[typeFilter];
  if (mappedType) {
    filtered = filtered.filter((t: any) => t.team_type === mappedType);
  }

  if (search.trim()) {
    const q = search.toLowerCase();
    filtered = filtered.filter((t: any) =>
      t.name?.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q),
    );
  }

  return filtered;
}

export function sortTeamsByActivity(teams: Team[]): Team[] {
  return [...teams].sort((a: any, b: any) => {
    const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
    const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
    return bTime - aTime;
  });
}

// ---------------------------------------------------------------------------
// Permission checks
// ---------------------------------------------------------------------------

export function canEditTeam(item: { status?: string }): boolean {
  return !(item.status === 'TEAM_STATUS_ARCHIVED' || item.status === 'archived' || item.status === 'deleted');
}

export function canDeleteTeam(item: { status?: string }): boolean {
  return item.status !== 'deleted';
}

export function canCancelDelegation(status: string): boolean {
  return ['DELEGATION_STATUS_PENDING', 'DELEGATION_STATUS_APPROVED', 'DELEGATION_STATUS_ACTIVE'].includes(status);
}

// ---------------------------------------------------------------------------
// Validation (returns i18n key strings)
// ---------------------------------------------------------------------------

export function validateTeamForm(data: {
  name: string;
  description?: string;
  max_members?: number;
  metadata?: { key: string; value: string }[];
}): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.name.trim()) {
    errors.name = 'validation.required';
  } else if (data.name.trim().length < 2) {
    errors.name = 'validation.minLength';
  } else if (data.name.trim().length > 100) {
    errors.name = 'validation.maxLength';
  }
  if (data.max_members !== undefined && data.max_members < 0) {
    errors.max_members = 'validation.minValue';
  }
  if (data.metadata) {
    const keys = new Set<string>();
    for (let i = 0; i < data.metadata.length; i++) {
      const entry = data.metadata[i];
      if (entry.key.trim() && keys.has(entry.key.trim())) {
        errors[`metadata_${i}_key`] = 'validation.duplicateKey';
      }
      if (entry.key.trim()) keys.add(entry.key.trim());
      if (entry.key.trim() && !entry.value.trim()) {
        errors[`metadata_${i}_value`] = 'validation.required';
      }
      if (!entry.key.trim() && entry.value.trim()) {
        errors[`metadata_${i}_key`] = 'validation.required';
      }
    }
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Parent team helpers
// ---------------------------------------------------------------------------

export interface ParentTeamOption {
  id: string;
  name: string;
}

export function mapTeamsToParentOptions(teams: Team[]): ParentTeamOption[] {
  return teams
    .filter((t: any) => t.status !== 'TEAM_STATUS_ARCHIVED')
    .map((t: any) => ({
      id: t.entity?.id || t.id || '',
      name: t.name || '',
    }))
    .filter((o) => o.id && o.name);
}

export function filterParentOptions(
  options: ParentTeamOption[],
  query: string,
): ParentTeamOption[] {
  if (!query.trim()) return options;
  const q = query.toLowerCase();
  return options.filter((o) => o.name.toLowerCase().includes(q));
}

// ---------------------------------------------------------------------------
// Metadata helpers
// ---------------------------------------------------------------------------

export function metadataListToMap(
  entries: { key: string; value: string }[],
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const entry of entries) {
    if (entry.key.trim() && entry.value.trim()) {
      map[entry.key.trim()] = entry.value.trim();
    }
  }
  return map;
}

export function validateRoleForm(data: {
  name: string;
}): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.name.trim()) {
    errors.name = 'validation.required';
  }
  return errors;
}

export function validateInvitationForm(data: {
  email: string;
}): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.email.trim()) {
    errors.email = 'validation.required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) {
    errors.email = 'validation.invalidEmail';
  }
  return errors;
}

export function validateDelegationForm(data: {
  delegate_id: string;
  start_at: string;
  end_at: string;
}): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.delegate_id.trim()) errors.delegate_id = 'validation.required';
  if (!data.start_at) errors.start_at = 'validation.required';
  if (!data.end_at) errors.end_at = 'validation.required';
  if (data.start_at && data.end_at && new Date(data.start_at) >= new Date(data.end_at)) {
    errors.end_at = 'validation.endAfterStart';
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Quick-link navigation items for team detail page
// ---------------------------------------------------------------------------

export interface TeamQuickLink {
  labelKey: string;
  path: string;
  descriptionKey: string;
  iconPath: string;
  countField?: string;
}

export const teamQuickLinks: TeamQuickLink[] = [
  {
    labelKey: 'team.nav.members',
    path: 'members',
    descriptionKey: 'team.nav.membersDesc',
    iconPath: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z',
    countField: 'members_count',
  },
  {
    labelKey: 'team.nav.roles',
    path: 'roles',
    descriptionKey: 'team.nav.rolesDesc',
    iconPath: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    countField: 'roles_count',
  },
  {
    labelKey: 'team.nav.applications',
    path: 'applications',
    descriptionKey: 'team.nav.applicationsDesc',
    iconPath: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    countField: 'pending_requests_count',
  },
  {
    labelKey: 'team.nav.schedules',
    path: 'schedules',
    descriptionKey: 'team.nav.schedulesDesc',
    iconPath: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  },
  {
    labelKey: 'team.nav.delegations',
    path: 'delegations',
    descriptionKey: 'team.nav.delegationsDesc',
    iconPath: 'M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z',
  },
  {
    labelKey: 'team.nav.settings',
    path: 'settings',
    descriptionKey: 'team.nav.settingsDesc',
    iconPath: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
  },
  {
    labelKey: 'team.nav.audit',
    path: 'audit',
    descriptionKey: 'team.nav.auditDesc',
    iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  },
  {
    labelKey: 'team.nav.ownership',
    path: 'ownership',
    descriptionKey: 'team.nav.ownershipDesc',
    iconPath: 'M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z',
  },
];

// ---------------------------------------------------------------------------
// Audit log filter options
// ---------------------------------------------------------------------------

export const auditTargetTypes = [
  'all', 'team', 'membership', 'role', 'invitation', 'settings',
] as const;

export type AuditTargetType = typeof auditTargetTypes[number];

// ---------------------------------------------------------------------------
// Settings tabs
// ---------------------------------------------------------------------------

export const settingsTabs = [
  { id: 'general' as const, labelKey: 'team.settings.general' },
  { id: 'security' as const, labelKey: 'team.settings.security' },
  { id: 'appearance' as const, labelKey: 'team.settings.appearance' },
  { id: 'notifications' as const, labelKey: 'team.settings.notifications' },
];

export type SettingsTab = typeof settingsTabs[number]['id'];

// ---------------------------------------------------------------------------
// Entity ID extractor
// ---------------------------------------------------------------------------

export function getEntityId(item: any): string {
  return item?.entity?.id || item?.id || '';
}

export function getDisplayName(actor: any): string {
  return actor?.display_name || actor?.name || '--';
}

export function getTeamName(item: any): string {
  return String(item?.name ?? '');
}

// ---------------------------------------------------------------------------
// Member count formatter
// ---------------------------------------------------------------------------

export function getMemberCountKey(count: number): string {
  return count === 1 ? 'team.memberCount_one' : 'team.memberCount_other';
}

// ---------------------------------------------------------------------------
// Entity reference
// ---------------------------------------------------------------------------

/**
 * Id-only Entity reference for request payloads. The generated Entity type
 * marks every scope field required, but team-service handlers read only `id`
 * (e.g. remove_team_member.go builds `Entity{Id: member_id}`), so a full
 * Entity would be filler the server ignores.
 */
export function refEntity(id: string): Entity {
  return { id } as Entity;
}

// ---------------------------------------------------------------------------
// Team workspace tabs
// ---------------------------------------------------------------------------

export type TeamTab =
  | 'overview'
  | 'tasks'
  | 'discussions'
  | 'documents'
  | 'projects'
  | 'resources'
  | 'people'
  | 'hierarchy'
  | 'settings';

export interface TeamTabConfig {
  id: TeamTab;
  labelKey: string;
  icon: string;
}

export const TEAM_TABS: TeamTabConfig[] = [
  { id: 'overview', labelKey: 'team.tab.overview', icon: 'overview' },
  { id: 'tasks', labelKey: 'team.tab.tasks', icon: 'task' },
  { id: 'discussions', labelKey: 'team.tab.discussions', icon: 'discussion' },
  { id: 'documents', labelKey: 'team.tab.documents', icon: 'document' },
  { id: 'projects', labelKey: 'team.tab.projects', icon: 'folder' },
  { id: 'resources', labelKey: 'team.tab.resources', icon: 'link' },
  { id: 'people', labelKey: 'team.tab.people', icon: 'people' },
  { id: 'hierarchy', labelKey: 'team.tab.hierarchy', icon: 'hierarchy' },
  { id: 'settings', labelKey: 'team.tab.settings', icon: 'settings' },
];

export const VALID_TEAM_TABS = new Set<string>(TEAM_TABS.map((t) => t.id));

/** Old tab ids that still arrive via bookmarked URLs. */
const LEGACY_TEAM_TAB_ALIASES: Record<string, TeamTab> = {
  members: 'people',
  activity: 'overview',
};

export interface TeamStats {
  memberCount: number;
  projectCount: number;
  taskCount: number;
  openTaskCount: number;
}

export function computeTeamStats(
  members: any[],
  projects: any[],
  tasks: any[],
): TeamStats {
  const openStatuses = new Set([
    'open', 'TASK_STATUS_OPEN',
    'in_progress', 'TASK_STATUS_IN_PROGRESS',
    'in_review', 'TASK_STATUS_IN_REVIEW',
  ]);
  return {
    memberCount: members.length,
    projectCount: projects.length,
    taskCount: tasks.length,
    openTaskCount: tasks.filter(
      (t: any) => openStatuses.has(t.status ?? ''),
    ).length,
  };
}

export function getTeamDisplayName(team: any): string {
  return team?.name || team?.title || '';
}

export function parseTeamTab(raw: string | null): TeamTab {
  if (raw && VALID_TEAM_TABS.has(raw)) return raw as TeamTab;
  if (raw && LEGACY_TEAM_TAB_ALIASES[raw]) return LEGACY_TEAM_TAB_ALIASES[raw];
  return 'overview';
}

// ---------------------------------------------------------------------------
// Activity event types
// ---------------------------------------------------------------------------

export type ActivityEventType =
  | 'task_created'
  | 'task_completed'
  | 'member_joined'
  | 'project_created';

export interface ActivityEvent {
  id: string;
  type: ActivityEventType;
  actorName: string;
  descriptionKey: string;
  timestamp: string;
}

export const ACTIVITY_EVENT_ICONS: Record<ActivityEventType, string> = {
  task_created: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  task_completed: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  member_joined: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z',
  project_created: 'M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z',
};

export const ACTIVITY_EVENT_COLORS: Record<ActivityEventType, string> = {
  task_created: 'text-blue-500 bg-blue-500/10',
  task_completed: 'text-emerald-500 bg-emerald-500/10',
  member_joined: 'text-violet-500 bg-violet-500/10',
  project_created: 'text-amber-500 bg-amber-500/10',
};
